import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { LoadingPanel } from "../../components/layout/Loading";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import {
  clearFilterBandhai,
  clearBandhaiDetail,
  setFilterBandhai,
  setBandhaiID,
} from "./bandhaiSlice";
import { deleteBandhai, findAllBandhai } from "./services/bandhai.services";
import { FaEye } from "react-icons/fa";

const Bandhai: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  useEffect(() => {
    return () => {
      dispatch(closeDialog());
      dispatch(clearBandhaiDetail());
      dispatch(setCurrentPage(0));
    };
  }, []);

  const handleProcessCreate = () => {
    navigate("/bandhai/create");
  };
  return (
    <>
      <DeleteBandhaiDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Bandhai Issue List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleProcessCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <BandhaiGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const BandhaiGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const BandhaiList = useAppSelector((state) => state.bandhai.BandhaiList);
  const loading = useAppSelector((state) => state.bandhai.loading);

  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const FilterBandhai = useAppSelector((state) => state.bandhai.FilterBandhai);

  useEffect(() => {
    const payload = {
      ...FilterBandhai,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(findAllBandhai(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const MyEditCommandCell = (props: any) => {
    const handleEditItem = (bandhai_issue_guid: string) => {
      navigate("/bandhai/edit", {
        state: { ID: bandhai_issue_guid },
      });
    };

    const handleOpenDeleteDialog = (bandhai_issue_guid: number) => {
      dispatch(openDialog("deleteBandhaiIssue"));
      dispatch(setBandhaiID(bandhai_issue_guid));
    };

    const handlePrint = (bandhai_issue_guid: string) => {
      window.open(`/bandhaiissue/${bandhai_issue_guid}`, "_blank");
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            themeColor="success"
            onClick={() => handleEditItem(props.dataItem?.bandhai_issue_guid)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem.bandhai_issue_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="View"
            themeColor="tertiary"
            onClick={() => handlePrint(props.dataItem.bandhai_issue_guid)}
          >
            <FaEye className="absolute-position" style={{ fontSize: "24px" }} />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 5 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const BandhaiFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.bandhai.FilterBandhai}
        setFilterAction={setFilterBandhai}
        clearFilterAction={clearFilterBandhai}
        fetchDataAction={findAllBandhai}
        pagination={{ case: "lowercase" }}
      />
    ),
    []
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        scrollable="scrollable"
        className="responsive-table"
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        filter={filter}
        data={filter ? filterBy(BandhaiList, filter) : BandhaiList}
        total={BandhaiList[0]?.itemcount || BandhaiList.length}
        onFilterChange={handleFilterChange}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          width={100}
          field="issue_no"
          title="Issue No"
          cells={{
            data: (props: any) => (
              <td>
                <div
                  className="hoverableName"
                  onClick={() =>
                    navigate("/bandhai/edit", {
                      state: { ID: props.dataItem?.bandhai_issue_guid },
                    })
                  }
                >
                  {props.dataItem?.issue_no}
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="issue_date"
          title="Issue Date"
          width={120}
          cells={{ filterCell: BandhaiFilterCell }}
        />
        <Column
          field="karigar_name"
          title="Karigar Name"
          width={200}
          cells={{ filterCell: BandhaiFilterCell }}
        />
        <Column
          field="approx_delivery_date"
          title="Approx Delivery Date"
          width={180}
          cells={{ filterCell: BandhaiFilterCell }}
        />

        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="bandhai_issue_guid"
            title="Actions"
            cells={{ data: MyEditCommandCell }}
            width={"110px"}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeleteBandhaiDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const BandhaiID = useAppSelector((state) => state.bandhai.BandhaiID);
  const FilterBandhai = useAppSelector((state) => state.bandhai.FilterBandhai);

  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const handleDeleteAction = async (bandhai_issue_guid: any) => {
    const getpayload = {
      ...FilterBandhai,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    if (bandhai_issue_guid) {
      const response = await dispatch(deleteBandhai(bandhai_issue_guid));
      dispatch(setCurrentPage(0));
      if (response.payload?.Data.Status === 200) {
        dispatch(findAllBandhai(getpayload));
        dispatch(closeDialog());
        dispatch(clearBandhaiDetail());
      } else {
        dispatch(findAllBandhai(getpayload));
        dispatch(closeDialog());
        dispatch(clearBandhaiDetail());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteBandhaiIssue" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Bandhai Issue?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Bandhai Issue?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => {
                    handleDeleteAction(BandhaiID);
                  }}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Bandhai;
